'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { BrandIcon } from './BrandIcon';
import { WavespeedPrice } from './WavespeedPrice';
import { useWavespeedQuote } from '@/hooks/useWavespeedQuote';
import { Loader2, X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

interface ModelInfo {
  id: string;
  label: string;
  brand?: { letter: string; color: string; name: string };
  description?: string;
  estimatedCost: number;
  type?: 'image' | 'video';
  formula?: string;
  badge?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  model?: ModelInfo;
  /** Other variants of the same brand-family (for cross-linking). */
  siblings?: ModelInfo[];
  onSelect?: (modelId: string) => void;
}

export function ModelInfoPopup({ open, onClose, model, siblings = [], onSelect }: Props) {
  const { t } = useI18n();
  const quote = useWavespeedQuote(model?.id ?? '', undefined, open);
  const ref = useRef<HTMLDivElement>(null);
  const [examples, setExamples] = useState<Array<{
    id: string;
    outputs: string[];
    prompt?: string;
    thumbnailUrl?: string;
  }>>([]);
  const [examplesLoading, setExamplesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', esc);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !model?.id) return;
    const controller = new AbortController();
    setExamples([]);
    setExamplesLoading(true);
    fetch(`/api/ai/wavespeed/examples?model=${encodeURIComponent(model.id)}&size=4`, {
      signal: controller.signal,
    })
      .then((response) => response.json())
      .then((json) => {
        if (!controller.signal.aborted && json?.success && Array.isArray(json.data)) {
          setExamples(json.data.slice(0, 4));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setExamplesLoading(false);
      });
    return () => controller.abort();
  }, [open, model?.id]);

  if (!open || !model) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/35 p-6 backdrop-blur-sm">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wavespeed-model-title"
        className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-[1.75rem] border border-ink-950/10 bg-white shadow-lift"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-ink-950/10 p-5">
          <div className="flex items-start gap-3 min-w-0">
            <BrandIcon modelId={model.id} fallbackLetter={model.brand?.letter} size={40} />
            <div className="min-w-0">
              <h3 id="wavespeed-model-title" className="flex items-center gap-2 text-lg font-semibold text-ink-950">
                {model.label}
                {model.badge && (
                  <span className="rounded-full bg-coral-100 px-1.5 py-0.5 text-[10px] font-semibold text-coral-700">
                    {model.badge}
                  </span>
                )}
              </h3>
              <p className="mt-0.5 break-all text-xs text-ink-500">
                {model.id}
              </p>
              <span className="mt-2 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">
                WaveSpeed live catalog
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('studio.modelInfo.close')}
            className="rounded-full p-1 text-ink-400 transition hover:bg-ink-50 hover:text-ink-950"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between border-b border-ink-950/10 px-5 py-4">
          <span className="text-xs uppercase tracking-wide text-ink-500">
            {t('studio.modelInfo.basePrice')}
          </span>
          <span className="text-xl font-semibold text-brand-600">
            {quote.price === undefined ? '—' : <WavespeedPrice quote={quote} theme="light" />}
          </span>
        </div>

        {/* Real outputs published for the selected WaveSpeed model. */}
        <div className="border-b border-ink-950/10 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-[11px] uppercase tracking-wide text-ink-500">
              {t('studio.modelInfo.examples')}
            </h4>
            <span className="text-[10px] text-ink-400">{t('studio.modelInfo.examplesHint')}</span>
          </div>
          {examplesLoading ? (
            <div className="flex h-28 items-center justify-center rounded-2xl bg-ink-50">
              <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
            </div>
          ) : examples.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {examples.map((example) => {
                const output = example.outputs?.[0];
                if (!output) return null;
                return model.type === 'video' ? (
                  <video
                    key={example.id}
                    src={output}
                    poster={example.thumbnailUrl}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onMouseEnter={(event) => event.currentTarget.play().catch(() => {})}
                    onMouseLeave={(event) => {
                      event.currentTarget.pause();
                      event.currentTarget.currentTime = 0;
                    }}
                    className="aspect-[9/12] w-full rounded-xl bg-ink-950 object-cover"
                    title={example.prompt || model.label}
                  />
                ) : (
                  <Image
                    key={example.id}
                    src={output}
                    referrerPolicy="no-referrer"
                    alt={example.prompt || model.label}
                    width={180}
                    height={240}
                    unoptimized
                    className="aspect-[9/12] w-full rounded-xl bg-ink-50 object-cover"
                  />
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl bg-ink-50 px-4 py-6 text-center text-xs text-ink-500">
              {t('studio.modelInfo.noExamples')}
            </div>
          )}
        </div>

        {/* Description */}
        {model.description && (
          <div className="border-b border-ink-950/10 p-5">
            <h4 className="mb-2 text-[11px] uppercase tracking-wide text-ink-500">
              {t('studio.modelInfo.about')}
            </h4>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-700">
              {model.description}
            </p>
          </div>
        )}

        {/* Formula (raw) */}
        {model.formula && (
          <div className="border-b border-ink-950/10 p-5">
            <h4 className="mb-2 text-[11px] uppercase tracking-wide text-ink-500">
              {t('studio.modelInfo.pricingFormula')}
            </h4>
            <code className="block break-all rounded-2xl bg-ink-50 p-3 text-[10px] text-ink-600">
              {model.formula}
            </code>
            <p className="mt-1 text-[10px] text-ink-500">
              {t('studio.modelInfo.finalPrice')}
            </p>
          </div>
        )}

        {/* Siblings */}
        {siblings.length > 0 && (
          <div className="p-5">
            <h4 className="mb-2 text-[11px] uppercase tracking-wide text-ink-500">
              {t('studio.modelInfo.relatedVariants')}
            </h4>
            <div className="space-y-1.5">
              {siblings.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    onSelect?.(s.id);
                    onClose();
                  }}
                  className="flex w-full items-center justify-between rounded-2xl bg-ink-50 px-3 py-2 text-left transition hover:bg-brand-50"
                >
                  <span className="text-sm font-medium text-ink-950">{s.label}</span>
                  <span className="text-[10px] text-ink-500">
                    <RelatedModelPrice modelId={s.id} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RelatedModelPrice({ modelId }: { modelId: string }) {
  const quote = useWavespeedQuote(modelId);
  return quote.price === undefined ? '—' : <WavespeedPrice quote={quote} theme="light" />;
}
